import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_INTERVAL_MS = 60 * 60 * 1000; // re-check for a new deploy hourly

// Detects when a newly-deployed service worker has installed and is waiting,
// and lets the app activate it (SKIP_WAITING) then reload — so users get new
// features without uninstalling/reinstalling the installed PWA.
//
// Detection covers every path: a worker already waiting at startup, one that
// becomes ready mid-session (via the periodic / on-focus `registration.update()`
// checks and the `updatefound` event), and the `sw-update` event dispatched
// from index.js's registration callback. Once the new worker takes control
// (`controllerchange`) the page reloads once.
export function useServiceWorkerUpdate(checkIntervalMs = DEFAULT_INTERVAL_MS) {
  const [updateReady, setUpdateReady] = useState(false);
  const waitingRef = useRef(null);
  const reloadingRef = useRef(false);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return undefined;

    let registration = null;

    const markReady = (worker) => {
      if (!worker) return;
      waitingRef.current = worker;
      setUpdateReady(true);
    };

    const check = () => {
      if (registration) registration.update().catch(() => {});
    };

    const onUpdateFound = () => {
      const installing = registration && registration.installing;
      if (!installing) return;
      installing.addEventListener('statechange', () => {
        // Only a *new* worker counts — `controller` is null on first install.
        if (installing.state === 'installed' && navigator.serviceWorker.controller) {
          markReady(installing);
        }
      });
    };

    const onSwUpdateEvent = (e) => {
      const reg = e.detail;
      if (reg && reg.waiting) markReady(reg.waiting);
    };

    const onControllerChange = () => {
      if (reloadingRef.current) return;
      reloadingRef.current = true;
      window.location.reload();
    };

    navigator.serviceWorker.getRegistration().then((reg) => {
      if (!reg) return;
      registration = reg;
      if (reg.waiting && navigator.serviceWorker.controller) markReady(reg.waiting);
      reg.addEventListener('updatefound', onUpdateFound);
      check();
    });

    const interval = window.setInterval(check, checkIntervalMs);
    window.addEventListener('focus', check);
    window.addEventListener('sw-update', onSwUpdateEvent);
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', check);
      window.removeEventListener('sw-update', onSwUpdateEvent);
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
      if (registration) registration.removeEventListener('updatefound', onUpdateFound);
    };
  }, [checkIntervalMs]);

  // Tell the waiting worker to activate; the controllerchange listener reloads.
  const applyUpdate = useCallback(() => {
    const worker = waitingRef.current;
    if (worker) worker.postMessage({ type: 'SKIP_WAITING' });
  }, []);

  return { updateReady, applyUpdate };
}
